import api from "@/api";
import moment from 'moment';
import i18n from "@/plugins/i18n";
import { defineStore } from "pinia";

import {
  License,
  FullLicenseRequest } from "@/interfaces/license.interface";
import { useFilterStore } from "./filterStore";
import { useSchoolStore } from "./schoolStore";

interface LicenseRequestPayload {
  requestedBy: string | undefined;
  additionalSeats: { product: string; additionalCount: number }[];
}

export const useLicenseStore = defineStore({
  id: "licenseStore",
  state: () => ({
    license: null as License | null,
    upcomingLicense: null as License | null,
    licenseRequests: [] as FullLicenseRequest[],
    loader: {
      creatingLicense: false,
      requestingSeats: false,
      fetchingLicenses: false,
      processingRequest: false,
      fetchingLicenseRequests: false,
      savingProductAssignments: false
    },
    loadingMessage: "",
  }),
  getters: {
    getLicense(): License | null {
      return this.license;
    },
    getLicenseRequests(): FullLicenseRequest[] {
      return this.licenseRequests;
    },
    selectedSubjectHasSeatsAvailable(): boolean {
      const filterStore = useFilterStore();
      const selectedSubject = filterStore.selectedFilters.subject;
      
      if (this.license && this.license.products) {
        const product = this.license.products.find(
          (product) => product.product.subject === selectedSubject
        );

        return product ? product.seatsUsed < product.maxSeats : false;
      }
      return false;
    },
    selectedSubjectProduct(): any {
      const filterStore = useFilterStore();
      const selectedSubject = filterStore.selectedFilters.subject;
      
      if (this.license && this.license.products) {
        return this.license.products.find(
          (product) => product.product.subject === selectedSubject
        );
      }
      return null;
    },
    licenseStateMatrix() {
      const matrix = {
        showCreateLicensePrompt: false,
        hasActiveLicenseNotExpiring: false,
        showExpiringSoonAlert: false,
        showExpiredAlert: false,
        showPendingLearnerAssignmentUI: false,
        showPendingIssuanceUI: false,
        showAddLicenseButton: true,
        showAllocateLearnersButton: false,

        // New scenarios:
        showAssignLearnersAlert: false,
        showUpcomingIssuanceInfo: false,
        showUpcomingIssuanceReminder: false
      };

      const currentLicense = this.license;
      const upcomingLicense = this.upcomingLicense;
      const today = moment().startOf('day');

      // If no licenses at all
      if (!currentLicense && !upcomingLicense) {
        matrix.showCreateLicensePrompt = true;
        matrix.showAddLicenseButton = true;
        return matrix;
      }

      if (currentLicense) {
        const endDate = moment(currentLicense.endDate).startOf('day');
        const daysToExpiry = endDate.diff(today, 'days');

        if (currentLicense.status === 'active') {
          if (daysToExpiry > 30) {
            // Active license not near expiry
            matrix.hasActiveLicenseNotExpiring = true;
          } else if (daysToExpiry <= 30 && daysToExpiry > 0) {
            // Active license about to expire
            matrix.showExpiringSoonAlert = true;
            matrix.showAddLicenseButton = true;
          } else if (daysToExpiry <= 0) {
            // License expired
            matrix.showExpiredAlert = true;
            matrix.showAddLicenseButton = true;
          }
        } else if (currentLicense.status === 'expired') {
          matrix.showExpiredAlert = true;
          matrix.showAddLicenseButton = true;
        }
      } else {
        // No current license but we have upcoming license
        matrix.showAddLicenseButton = false; 
      }

      // Check upcoming license states
      if (upcomingLicense) {
        matrix.showAddLicenseButton = false;

        const startDate = moment(upcomingLicense.startDate).startOf('day');
        const daysUntilStart = startDate.diff(today, 'days');


        if (upcomingLicense.status === 'pending-learner-assignment') {
          matrix.showPendingLearnerAssignmentUI = true;
          matrix.showAllocateLearnersButton = true;

          // if no learners assigned and start date is approaching
          // Check if learnerProductAssignments is empty
          const assignments = upcomingLicense.learnerProductAssignments || {};
          const hasNoAssignments = Object.keys(assignments).length === 0;

          if (hasNoAssignments) {
            matrix.showAssignLearnersAlert = true;
          }

        } else if (upcomingLicense.status === 'pending-issuance') {
          matrix.showPendingIssuanceUI = true;
          matrix.showAllocateLearnersButton = true;

          // If start date is far in the future (> 14 days, for example)
          if (daysUntilStart > 14) {
            matrix.showUpcomingIssuanceInfo = true; // Just info that it will start automatically
          } else if (daysUntilStart <= 14 && daysUntilStart > 0) {
            matrix.showUpcomingIssuanceReminder = true; // Reminder that issuance is coming soon
          }
        }
      }
      return matrix;
    }
  },
  actions: {
    /**
     * Fetch licenses for a specific school
     */
    async fetchLicenses(schoolId: string) {
      this.loader.fetchingLicenses = true;
      this.loadingMessage = i18n.global.t(
        "loaders.license_store.updating"
      );
      this.license = null;
      this.upcomingLicense = null;

      try {
        const response = await api.get(
          `/v2/organisation/school-license/school/${schoolId}`
        );

        if (response.status === 200) {
          const licenses = response.data.data;
          // this.license = response.data.data;

          // Separate current and upcoming licenses
          const currentLicenses = licenses.filter((license: License) => (license.status === 'active' || license.status === 'expired'));
          const upcomingLicenses = licenses.filter((license: License) => (license.status === 'pending-issuance' || license.status === 'pending-learner-assignment'));

          // Set the current license with the latest endDate
          if (currentLicenses.length > 0) {
            currentLicenses.sort((a: License, b: License) => {
              return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            });
            // Assuming licenses are sorted by endDate descending
            this.license = currentLicenses[0];
          }

          // Set the upcoming license to the upcoming license with the latest startDate
          if (upcomingLicenses.length > 0) {
            // Sort upcoming licenses by startDate descending
            upcomingLicenses.sort((a: License, b: License) => {
              return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            });
            this.upcomingLicense = upcomingLicenses[0];
          }

        } else {
          throw new Error("Failed to fetch licenses.");
        }
      } catch (error) {
        this.license = null;
        this.upcomingLicense = null;
        console.error("Error fetching licenses: ", error);
      } finally {
        this.loader.fetchingLicenses = false;
        this.loadingMessage = "";
      }
    },

    /**
     * Request more seats for a specific license
     */
    async requestSeats(licenseId: string, payload: LicenseRequestPayload) {
      this.loader.requestingSeats = true;
      this.loadingMessage = i18n.global.t(
        "loaders.license_store.request_seats"
      );

      try {
        const response = await api.post(
          `/v2/organisation/school-license/request-more/${licenseId}`,
          payload
        );

        if (response.status === 201) {
          return response.data;
        } else {
          throw new Error("Failed to request additional seats.");
        }
      } catch (error) {
        console.error("Error requesting seats: ", error);
      } finally {
        this.loader.requestingSeats = false;
        this.loadingMessage = "";
      }
    },

    /**
     * Fetch license seat requests
     */
    async fetchLicenseRequests(filters: {
      school?: string;
      status?: string;
      license?: string;
    }) {
      this.loader.fetchingLicenseRequests = true;
      this.licenseRequests = [];

      try {
        const queryString = new URLSearchParams(filters as any).toString();
        const response = await api.get(
          `/v2/organisation/school-license/seats-requests?${queryString}`
        );

        if (response.status === 200) {
          this.licenseRequests = response.data.data;
        } else {
          throw new Error("Failed to fetch license requests.");
        }
      } catch (error) {
        console.error("Error fetching license requests: ", error);
        this.licenseRequests = [];
      } finally {
        this.loader.fetchingLicenseRequests = false;
      }
    },

    /**
     * Process (approve/reject) a license request
     */
    async processLicenseRequest(
      requestId: string,
      status: "approved" | "rejected"
    ) {
      this.loader.processingRequest = true;

      try {
        const response = await api.put(
          `/v2/organisation/school-license/process-request/${requestId}`,
          { status }
        );

        if (response.status === 200) {
          return response.data;
        } else {
          throw new Error("Failed to process license request.");
        }
      } catch (error) {
        console.error("Error processing license request: ", error);
      } finally {
        this.loader.processingRequest = false;
      }
    },
    /**
     * Assign license product ids to a user
     */
    async assignLicenses(userId: string, licenseProductIds: Array<string>) {
      this.loader.processingRequest = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.assignLicenses"
      );

      try {
        const assignLicensesResponse = await api.post(
          `v2/organisation/students/assign-licenses/${userId}`,
          { licenseProductIds }
        );

        if (assignLicensesResponse.status !== 200) {
          throw new Error("Failed to assign licenses from API.");
        }

        //decrement seats used for each product assigned
        licenseProductIds.forEach((licenseProductId) => {
          this.incrementSchoolLicenseSeatsUsed(licenseProductId);
        });

        const assignLicensesData = assignLicensesResponse.data.data;
        return assignLicensesData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.processingRequest = false;
      }
    },
    /**
     * Bulk assign license product ids to a list of users
     */
    async bulkAssignLicenses(
      userIds: Array<string>,
      licenseProductIds: Array<string>
    ) {
      this.loader.processingRequest = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.assignLicences"
      );

      try {
        const bulkAssignLicensesResponse = await api.post(
          `v2/organisation/students/bulk/assign-licenses`,
          { userIds, licenseProductIds }
        );

        if (bulkAssignLicensesResponse.status !== 200) {
          throw new Error("Failed to assign licenses from API.");
        }

        //decrement seats used for each product assigned
        userIds.forEach((id) => {
          licenseProductIds.forEach((licenseProductId) => {
            this.incrementSchoolLicenseSeatsUsed(licenseProductId);
          });
        });

        const bulkAssignLicensesData = bulkAssignLicensesResponse.data.data;

        return bulkAssignLicensesData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.processingRequest = false;
      }
    },

    incrementSchoolLicenseSeatsUsed(licenseProductId: string) {
      if (this.license && this.license.products) {
        const product = this.license.products.find(
          (product) => product._id === licenseProductId
        );

        if (product) {
          product.seatsUsed += 1;
        }
      }
    },

    /*
      Update the license product for existing learners
    */
      decrementSchoolLicenseSeatsUsedForLearners(productId: string, numberOfLearnersToDecrement: number) {
        if (this.license && this.license.products) {
          const product = this.license.products.find(
            (product) => product._id === productId
          );
      
          if (product) {
            product.seatsUsed = Math.max(product.seatsUsed - numberOfLearnersToDecrement, 0);
          }
        }
      }, 
    /**
     * Create a new license for the school
     */
    async createLicense(payload: any, isRenewal = false) {
      payload.isRenewal = isRenewal;
      this.loader.creatingLicense = true;
      this.loadingMessage = i18n.global.t("loaders.license_store.creating_license");

      try {
        const response = await api.post(`/v2/organisation/school-license/new`, payload);

        if (response.status === 201) {
          await this.fetchLicenses(payload.school);

          return response.data;
        } else {
          throw new Error("Failed to create a new license.");
        }
      } catch (error) {
        console.error("Error creating new license: ", error);
        throw error; // Rethrow the error to be handled by the calling function
      } finally {
        this.loader.creatingLicense = false;
        this.loadingMessage = "";
      }
    },

    async saveLearnerProductAssignments(
      licenseId: string,
      learnerProductAssignments: { [key: string]: string[] }
    ) {
      this.loader.savingProductAssignments = true;
      this.loadingMessage = "Saving Learner Product Assignments"

      // this.loadingMessage = i18n.global.t(
      //   "loaders.license_store.assigning_products"
      // );

      try {
        const response = await api.patch(
          `/v2/organisation/school-license/assign-products/${licenseId}`,
          { learnerProductAssignments }
        );

        if (response.status === 200) {
          this.upcomingLicense = response.data.data;
          return response.data.data;
        } else {
          throw new Error("Failed to assign products to learners.");
        }
      } catch (error) {
        console.error("Error assigning products to learners: ", error);
        throw error;
      } finally {
        this.loader.savingProductAssignments = false;
        this.loadingMessage = "";
      }
    },

    decrementSchoolLicenseSeatsUsed(licenseProductId: string) {
      if (this.license && this.license.products) {
        const product = this.license.products.find(
          (product) => product._id === licenseProductId
        );

        if (product) {
          product.seatsUsed -= 1;
        }
      }
    },
    isSubjectLicenseSeatsExceeded(subjectId: string): boolean {
      if (this.license && this.license.products) {
        return this.license.products.some(
          (product) =>
            product?.product?.subject &&
            product.product.subject === subjectId &&
            product.seatsUsed > product.maxSeats
        );
      }
      return false;
    },
    isAnyLicenseSeatsExceeded(): boolean {
      if (this.license && this.license.products) {
        return this.license.products.some(
          (product) => product.seatsUsed > product.maxSeats
        );
      }
      return false;
    },
    /**
     * Update an existing license
     */
    async updateLicense(licenseId: string, payload: any) {
      this.loader.creatingLicense = true;
      this.loadingMessage = i18n.global.t("loaders.license_store.updating_license");

      try {
        const response = await api.patch(`/v2/organisation/school-license/update/${licenseId}`, payload);

        if (response.status === 200) {
          const schoolStore = useSchoolStore();

          if (schoolStore.getCurrentSchool?._id) {
            await this.fetchLicenses(schoolStore.getCurrentSchool._id);
          }

          return response.data;
        } else {
          throw new Error("Failed to update the license.");
        }
      } catch (error) {
        console.error("Error updating license: ", error);
        throw error; // Rethrow the error to be handled by the calling function
      } finally {
        this.loader.creatingLicense = false;
        this.loadingMessage = "";
      }
    },
    getLicenseForSubject(subjectId: string) {
      return this.license?.products.find(
        (product) => product.product.subject === subjectId
      );
    },
  },
  persist: true,
});
